import { useState } from "react";
import { RefreshCw, CheckCircle2, Clock, ShieldCheck } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

function formatSyncTime(isoString: string): string {
  const date = new Date(isoString);
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function formatFullDateTime(isoString: string): string {
  return new Date(isoString).toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface PdsSyncButtonProps {
  /** ISO timestamp of the last successful PDS sync, or null/undefined if never synced. */
  lastSyncedAt?: string | null;
  /** Called with the new ISO timestamp once a sync completes. */
  onSynced?: (timestamp: string) => void;
  isDarkMode?: boolean;
  className?: string;
  onClick?: () => void;
}

export function PdsSyncButton({
  lastSyncedAt,
  onSynced,
  isDarkMode = false,
  className,
  onClick,
}: PdsSyncButtonProps) {
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSync = async () => {
    if (onClick) {
      onClick();
      return;
    }
    if (isSyncing) return;
    setIsSyncing(true);
    // Simulate NHS Spine PDS lookup (~1.2s network round-trip)
    await new Promise((resolve) => setTimeout(resolve, 1200));
    onSynced?.(new Date().toISOString());
    setIsSyncing(false);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleSync}
            disabled={isSyncing}
            className={cn(
              "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:cursor-default",
              isSyncing
                ? isDarkMode
                  ? "border-blue-700 bg-blue-900/40 text-blue-300"
                  : "border-blue-300 bg-blue-50 text-blue-700"
                : isDarkMode
                  ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
              className,
            )}
          >
            <RefreshCw className={cn("w-3.5 h-3.5")} />
            {/* <RefreshCw className={cn("w-3.5 h-3.5", isSyncing && "animate-spin")} /> */}
            <span>PDS sync</span>
            {/* <span
              className={cn(
                "text-[10px] font-normal",
                isSyncing
                  ? isDarkMode
                    ? "text-blue-300"
                    : "text-blue-600"
                  : isDarkMode
                    ? "text-gray-400"
                    : "text-gray-500",
              )}
            >
              {isSyncing
                ? "syncing…"
                : lastSyncedAt
                  ? formatSyncTime(lastSyncedAt)
                  : "never"}
            </span> */}
          </button>
        </TooltipTrigger>
        {/* Hover popup temporarily disabled — commented out, not removed, so it can be restored easily.
        <TooltipContent side="bottom" align="end" className="max-w-xs p-0">
          <div className="px-3 py-2.5 space-y-2">
            <div className="flex items-center gap-1.5 font-semibold">
              <span
                className="inline-flex items-center rounded-sm bg-[#005EB8] px-1 py-0.5 text-[10px] font-bold italic leading-none tracking-tight text-white"
                aria-hidden="true"
              >
                NHS
              </span>
              <span>Personal Demographics Service</span>
            </div>

            {isSyncing ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <RefreshCw className="w-3.5 h-3.5 animate-spin shrink-0" />
                <span>Pulling the latest record from the NHS Spine…</span>
              </div>
            ) : lastSyncedAt ? (
              <div className="space-y-1.5 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-green-600 shrink-0" />
                  <span>Demographics verified against the NHS Spine.</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 shrink-0" />
                  <span>Last synced {formatFullDateTime(lastSyncedAt)}</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="w-3.5 h-3.5 shrink-0" />
                <span>This patient has never been synced with the NHS Spine.</span>
              </div>
            )}

            {!isSyncing && (
              <div className="flex items-center gap-2 border-t pt-2 text-foreground">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#005EB8] shrink-0" />
                <span className="font-medium">
                  Click to {lastSyncedAt ? "re-sync" : "sync"} contact &amp;
                  address details
                </span>
              </div>
            )}
          </div>
        </TooltipContent>
        */}
      </Tooltip>
    </TooltipProvider>
  );
}
